/**
 * Formats a date string to DD-MMM-YYYY format (e.g., "06-NOV-2025")
 * Handles various date formats including ISO strings (YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss)
 * Uses UTC methods to ensure consistent behavior across timezones
 *
 * @param date - Date string or Date object. Can be in various formats:
 *   - ISO string: "2025-11-04" or "2025-11-04T10:30:00" or "2025-11-04T10:30:00Z"
 *   - Date object
 *   - null or undefined (returns 'N/A')
 *
 * @returns Formatted date string in DD-MMM-YYYY format, or 'N/A' if date is invalid/null
 *
 * @example
 * formatDate('2025-11-04') // Returns "04-NOV-2025"
 * formatDate('2025-11-04T10:30:00') // Returns "04-NOV-2025"
 * formatDate(new Date('2025-11-04T10:30:00Z')) // Returns "04-NOV-2025" (consistent across timezones)
 * formatDate(null) // Returns "N/A"
 */
export const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A'

    let dateObj: Date

    // Handle string dates
    if (typeof date === 'string') {
        // Ensure consistent timezone handling:
        // - Date-only strings (YYYY-MM-DD) are treated as UTC by appending 'Z'
        // - Strings with time but no timezone are treated as UTC by appending 'Z' if missing
        // - Strings with timezone (ending in Z or +/-offset) are used as-is
        let dateString = date
        if (!date.includes('T')) {
            // Date-only format: treat as UTC
            dateString = date + 'T00:00:00Z'
        } else if (!date.includes('Z') && !date.match(/[+-]\d{2}:\d{2}$/)) {
            // Has time but no timezone indicator: treat as UTC
            dateString = date + 'Z'
        }
        dateObj = new Date(dateString)
    } else {
        dateObj = date
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
        return 'N/A'
    }

    // Format to DD-MMM-YYYY format using UTC methods
    // This ensures consistent behavior across different timezones
    // When a Date object is passed, it represents a specific moment in time (UTC internally)
    // Using UTC methods ensures the date displayed corresponds to the UTC date, not local timezone
    const monthAbbreviations = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
    const day = String(dateObj.getUTCDate()).padStart(2, '0')
    const month = monthAbbreviations[dateObj.getUTCMonth()]
    const year = String(dateObj.getUTCFullYear())

    return `${day}-${month}-${year}`
}

/**
 * Formats a date string to DD-MMM-YYYY format with time (e.g., "06-NOV-2025, 10:30 AM")
 * Uses UTC methods for both date and time to ensure consistency
 *
 * @param date - Date string or Date object. Can be in various formats:
 *   - ISO string: "2025-11-04T10:30:00" or "2025-11-04T10:30:00Z"
 *   - Date object
 *   - null or undefined (returns 'N/A')
 *
 * @returns Formatted date and time string in "DD-MMM-YYYY, HH:MM AM/PM" format, or 'N/A' if date is invalid/null
 *
 * @example
 * formatDateTime('2025-11-04T10:30:00Z') // Returns "04-NOV-2025, 10:30 AM"
 * formatDateTime(new Date('2025-11-04T10:30:00Z')) // Returns "04-NOV-2025, 10:30 AM" (consistent across timezones)
 * formatDateTime(null) // Returns "N/A"
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A'

    let dateObj: Date

    // Handle string dates
    if (typeof date === 'string') {
        // Ensure consistent timezone handling:
        // - Date-only strings (YYYY-MM-DD) are treated as UTC by appending 'Z'
        // - Strings with time but no timezone are treated as UTC by appending 'Z' if missing
        // - Strings with timezone (ending in Z or +/-offset) are used as-is
        let dateString = date
        if (!date.includes('T')) {
            // Date-only format: treat as UTC
            dateString = date + 'T00:00:00Z'
        } else if (!date.includes('Z') && !date.match(/[+-]\d{2}:\d{2}$/)) {
            // Has time but no timezone indicator: treat as UTC
            dateString = date + 'Z'
        }
        dateObj = new Date(dateString)
    } else {
        dateObj = date
    }

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
        return 'N/A'
    }

    // Format to DD-MMM-YYYY format using UTC methods
    const monthAbbreviations = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
    const day = String(dateObj.getUTCDate()).padStart(2, '0')
    const month = monthAbbreviations[dateObj.getUTCMonth()]
    const year = String(dateObj.getUTCFullYear())

    // Format time using UTC methods to ensure consistency with date
    const hours = dateObj.getUTCHours()
    const minutes = dateObj.getUTCMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    const displayMinutes = String(minutes).padStart(2, '0')

    return `${day}-${month}-${year}, ${displayHours}:${displayMinutes} ${ampm}`
}

const ET_TIMEZONE = 'America/New_York'
const MONTH_ABBREVIATIONS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/**
 * Parses a date string or Date into a Date object, treating date-only or no-timezone strings as UTC.
 */
function parseDateAsUTC(date: string | Date | null | undefined): Date | null {
    if (!date) return null
    if (typeof date === 'object') return date
    let dateString = date
    if (!date.includes('T')) {
        dateString = date + 'T00:00:00Z'
    } else if (!date.includes('Z') && !date.match(/[+-]\d{2}:\d{2}$/)) {
        dateString = date + 'Z'
    }
    const dateObj = new Date(dateString)
    return isNaN(dateObj.getTime()) ? null : dateObj
}

/**
 * Formats a UTC date/time for display in Eastern Time (ET).
 * Use for contact attempt logs and other backend timestamps that should be shown in ET.
 *
 * @param date - Date string (ISO, typically from backend in GMT/UTC) or Date object
 * @returns Formatted string in "DD-MMM-YYYY, H:MM AM/PM" in ET, or 'N/A' if invalid
 *
 * @example
 * formatDateTimeET('2025-03-03T19:30:00Z') // e.g. "03-MAR-2025, 2:30 PM" (ET)
 */
export const formatDateTimeET = (date: string | Date | null | undefined): string => {
    const dateObj = parseDateAsUTC(date)
    if (!dateObj) return 'N/A'

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    })
    const parts = formatter.formatToParts(dateObj)
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''

    const monthNum = parseInt(get('month'), 10)
    const month = MONTH_ABBREVIATIONS[monthNum - 1] ?? get('month')
    const day = get('day').padStart(2, '0')
    const year = get('year')
    const hour = get('hour')
    const minute = get('minute').padStart(2, '0')
    const dayPeriod = get('dayPeriod').toUpperCase()

    return `${day}-${month}-${year}, ${hour}:${minute} ${dayPeriod}`
}

/**
 * Converts a date/time interpreted as Eastern Time (ET) to a UTC ISO string.
 * Use when the user selects a date and time that should be treated as ET (e.g. "Next contact").
 *
 * @param year - Full year (e.g. 2025)
 * @param monthIndex - Month 0-11 (same as Date.getMonth())
 * @param day - Day of month 1-31
 * @param hour - Hour 0-23
 * @param minute - Minute 0-59
 * @returns ISO string in UTC (e.g. "2025-03-05T19:00:00.000Z" for 2:00 PM ET)
 */
export function dateTimeETToUTCISOString(year: number, monthIndex: number, day: number, hour: number, minute: number): string {
    const refUtc = new Date(Date.UTC(year, monthIndex, day, 12, 0))
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
    })
    const parts = formatter.formatToParts(refUtc)
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '0'
    const etHourNoon = parseInt(get('hour'), 10)
    const etMinNoon = parseInt(get('minute'), 10)
    const offsetMinutes = (etHourNoon - 12) * 60 + etMinNoon
    const totalMinutesFromMidnightUTC = hour * 60 + minute - offsetMinutes
    const utcMs = Date.UTC(year, monthIndex, day, 0, 0) + totalMinutesFromMidnightUTC * 60 * 1000
    return new Date(utcMs).toISOString()
}

/**
 * Converts a UTC ISO string to a Date suitable for date/time pickers so that the
 * displayed value matches Eastern Time. The returned Date has getFullYear(), getMonth(),
 * getDate(), getHours(), getMinutes() equal to the ET components.
 * Use when loading "next contact" from the backend for edit forms.
 *
 * @param iso - UTC ISO string (e.g. from backend)
 * @returns Date that displays as the ET date/time in pickers, or null if invalid
 */
export function utcISOStringToDateForETDisplay(iso: string | null | undefined): Date | null {
    const dateObj = parseDateAsUTC(iso)
    if (!dateObj) return null
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ET_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
    })
    const parts = formatter.formatToParts(dateObj)
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '0'
    const year = parseInt(get('year'), 10)
    const month = parseInt(get('month'), 10) - 1
    const day = parseInt(get('day'), 10)
    const hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    return new Date(year, month, day, hour, minute)
}
